<template>
  <b-card
    v-if="
      isComplaintAdmin ||
      masterUser ||
      isCommitteeMember ||
      (permissions.complaint && permissions.complaint.readall)
    "
    no-body
    class="denounced-card"
  >
    <b-card-header class="denounced-header">
      <div class="header-title">
        <i class="fas fa-user-times mr-2"></i>
        <strong>Investigados</strong>
      </div>
      <div class="header-actions">
        <b-button
          v-if="canEdit"
          variant="primary"
          size="sm"
          :disabled="loadingDenounceds"
          @click="$emit('open')"
          >Inserir
        </b-button>
      </div>
    </b-card-header>
    <b-list-group v-if="denounceds && denounceds.length">
      <b-list-group-item
        v-for="denounced in denounceds"
        :key="denounced._id"
        class="denounced-item d-flex justify-content-between align-items-start"
      >
        <div class="denounced-info flex-grow-1">
          <div class="denounced-name">
            <strong>{{ denounced.name }}</strong>
          </div>
          <div class="denounced-details">
            <span class="id-info">
              <i class="fas fa-id-card mr-1"></i>
              {{ denounced.cpfcnpj || "Não informado" }}
            </span>
            <span
              v-if="denounced.office || denounced.department"
              class="position-info"
            >
              <i class="fas fa-briefcase mr-1"></i>
              {{ denounced.office
              }}{{ denounced.department ? " - " + denounced.department : "" }}
            </span>
          </div>
        </div>
        <div v-if="canEdit">
          <b-button
            size="sm"
            variant="outline-danger"
            :disabled="removing || loadingDenounceds"
            @click="openRemoveModal(denounced)"
            v-b-tooltip.hover
            title="Desvincular investigado"
          >
            <i class="fas fa-unlink"></i>
          </b-button>
        </div>
      </b-list-group-item>
    </b-list-group>
    <em v-else class="p-3 d-block">Nenhum investigado cadastrado.</em>

    <!-- Modal Desvincular -->
    <b-modal
      id="unlink-denounced-modal"
      ref="unlinkModal"
      :title="selectedDenounced ? 'Desvincular Investigado' : 'Desvincular'"
      @hide="onHideUnlink"
      :ok-disabled="!justification.trim() || removing"
      ok-title="Confirmar"
      cancel-title="Cancelar"
      @ok="confirmUnlink"
    >
      <div v-if="selectedDenounced">
        <p class="mb-2">
          Confirma a desvinculação de
          <strong>{{ selectedDenounced.name }}</strong>
          <span v-if="selectedDenounced.cpfcnpj">
            ({{ selectedDenounced.cpfcnpj }})
          </span>
          desta denúncia?
        </p>
        <b-form-group
          label="Justificativa"
          description="Descreva o motivo da desvinculação (obrigatório)."
          :state="justificationState"
          invalid-feedback="Informe a justificativa"
        >
          <b-form-textarea
            v-model="justification"
            :disabled="removing"
            :state="justificationState"
            rows="3"
            max-rows="6"
            placeholder="Digite a justificativa"
          />
        </b-form-group>
        <b-alert v-if="errorMessage" variant="danger" show class="py-1">
          {{ errorMessage }}
        </b-alert>
      </div>
      <div v-else>
        <em>Nenhum denunciado selecionado.</em>
      </div>
      <template #modal-ok>
        <b-spinner v-if="removing" small />
        <span v-else>Confirmar</span>
      </template>
    </b-modal>
  </b-card>
</template>

<script>
import { removeDenounced } from "@/api/complaint";

export default {
  name: "DenouncedList",
  props: {
    denounceds: { type: Array, default: () => [] },
    canEdit: { type: Boolean, default: false },
    loadingDenounceds: { type: Boolean, default: false },
    isComplaintAdmin: { type: Boolean, default: false },
    masterUser: { type: Boolean, default: false },
    isCommitteeMember: { type: Boolean, default: false },
    permissions: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      selectedDenounced: null,
      justification: "",
      removing: false,
      errorMessage: "",
    };
  },
  computed: {
    justificationState() {
      if (this.justification === "") return null;
      return this.justification.trim().length > 0;
    },
  },
  methods: {
    getDisplayId(id) {
      if (!id) return "";
      const cleanId = id.replace(/\D/g, "");
      if (cleanId.length === 11) return `CPF: ${cleanId}`;
      if (cleanId.length === 14) return `CNPJ: ${cleanId}`;
      return `ID Interno: ${cleanId}`;
    },
    openRemoveModal(denounced) {
      this.selectedDenounced = denounced;
      this.justification = "";
      this.errorMessage = "";
      this.$refs.unlinkModal.show();
    },
    async confirmUnlink(evt) {
      if (!this.justification.trim()) {
        evt.preventDefault();
        this.errorMessage = "Justificativa é obrigatória";
        return;
      }
      if (!this.selectedDenounced) return;
      try {
        this.removing = true;
        this.$emit("loading", true);
        await removeDenounced(
          this.selectedDenounced.complaint,
          this.selectedDenounced._id,
          this.justification.trim()
        );
        this.$bvToast.toast("Denunciado desvinculado", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
        this.$emit("refresh");
        this.$refs.unlinkModal.hide();
      } catch (e) {
        this.errorMessage =
          (e && e.response && e.response.data) ||
          e.message ||
          "Erro ao desvincular";
      } finally {
        this.removing = false;
        this.$emit("loading", false);
      }
    },
    onHideUnlink() {
      if (!this.removing) {
        this.selectedDenounced = null;
        this.justification = "";
        this.errorMessage = "";
      }
    },
  },
};
</script>

<style scoped>
.denounced-info {
  width: 100%;
}

.denounced-name {
  margin-bottom: 0.25rem;
}

.denounced-details {
  font-size: 0.875rem;
  color: #6c757d;
}

.id-info,
.position-info {
  display: inline-block;
  margin-right: 1rem;
}

.id-info i,
.position-info i {
  color: #007bff;
}
.denounced-item {
  position: relative;
}
</style>
